import type { QueryClient } from "@tanstack/react-query";
import type { RouterHistory } from "@tanstack/react-router";
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import type { ApiClient } from "~/api/client";
import {
  SEARCH_DATE_FILTERS,
  SEARCH_STATUSES,
  type SearchDateFilter,
  type SearchStatus,
} from "~/api/libraryTypes";
import { parseResearchContext, RESEARCH_QUESTION_PARAM_LIMIT } from "~/api/researchTypes";
import { safeReturnTo } from "~/auth/oidc";
import { CallbackScene } from "~/features/auth/CallbackScene";
import { LoginScene } from "~/features/auth/LoginScene";
import {
  AuthCallbackScene,
  LogoutScene,
  SelectWorkspaceScene,
} from "~/features/auth/SessionScenes";
import { CollectionScene, type CollectionTab } from "~/features/collections/CollectionScene";
import { CollectionsScene } from "~/features/collections/CollectionsScene";
import { safeNewsView } from "~/features/collections/collectionInput";
import { SourceLensScene } from "~/features/knowledge/SourceLensScene";
import { WorkspaceNewsCollectionScene } from "~/features/collections/NewsCollectionScene";
import { IdeConsoleScene } from "~/features/console/IdeConsoleScene";
import { DiscoverScene } from "~/features/discover/DiscoverScene";
import { DocumentHistoryScene } from "~/features/documents/DocumentHistoryScene";
import { DocumentInsightsScene } from "~/features/documents/DocumentInsightsScene";
import { DocumentScene } from "~/features/documents/DocumentScene";
import { DocumentsScene } from "~/features/documents/DocumentsScene";
import { ArchiveScene, DraftsScene, TrashScene } from "~/features/documents/StatusScenes";
import { SharedDocumentScene } from "~/features/documents/shared/SharedDocumentScene";
import { HomeScene, type HomeTab } from "~/features/home/HomeScene";
import { NotFoundScene } from "~/features/NotFoundScene";
import { ReasoningHubScene } from "~/features/reasoning/ReasoningHubScene";
import { parseReasoningLaunch } from "~/features/reasoning/reasoningLaunch";
import { RunPage, type RunTab } from "~/features/runs/RunPage";
import { type SearchRouteParams, SearchScene } from "~/features/search/SearchScene";
import { SettingsScene } from "~/features/settings/SettingsScene";
import { useWorkspaceGate, WorkspaceGateScreen } from "~/features/work/workspaceGate";
import { loaderFor, routePathsFor } from "~/registry";
import { AuthenticatedLayout } from "~/shell/AuthenticatedLayout";
import { isPathHiddenOnSite } from "~/shell/nav";
import { SITE_CONFIG } from "~/site";

/** This app's id in the feature registry. */
const SITE = "slate" as const;

export interface RouterContext {
  queryClient: QueryClient;
  api: ApiClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: NotFoundScene,
});

/* ── Public: no session required ──────────────────────────────────────────── */

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: (search: Record<string, unknown>): { redirect?: string } =>
    typeof search.redirect === "string" ? { redirect: search.redirect } : {},
  // Already signed in? Then this page is a dead end that looks like a failed sign-in.
  // Bouncing here is what stops an OIDC round trip landing back on /login.
  beforeLoad: async ({ context, search }) => {
    const session = await context.api.auth.session();
    if (session) {
      throw redirect({ to: safeReturnTo(search.redirect) as never });
    }
  },
  component: LoginScene,
});

const logoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/logout",
  component: LogoutScene,
});

const authCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/callback",
  component: AuthCallbackScene,
});

/** Where the identity provider returns with the authorization code. */
const callbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/callback",
  component: CallbackScene,
});

const selectWorkspaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/select-workspace",
  component: SelectWorkspaceScene,
});

/** A share link must render for someone with no session at all — hence not under `authed`. */
const sharedDocumentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/s/$shareId",
  component: function SharedDocumentRoute() {
    const { shareId } = sharedDocumentRoute.useParams();
    return <SharedDocumentScene shareId={shareId} />;
  },
});

/* ── Authenticated: everything inside the shell ───────────────────────────── */

/**
 * Pathless guard route. Everything below it is inside the shell and cannot render without a
 * session; the API is asked on every navigation, so signing out anywhere ejects everywhere.
 */
const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "authed",
  beforeLoad: async ({ context, location }) => {
    const session = await context.api.auth.session();
    if (!session) {
      // Carry the destination so sign-in returns there rather than to the home page.
      throw redirect({ to: "/login", search: { redirect: location.href } });
    }
    // An area the site keeps behind the scenes (appcoil: marketplace, people and work only) is not
    // reachable by a pasted, pinned or notified link either — it lands on the site's start page.
    if (isPathHiddenOnSite(location.pathname)) {
      throw redirect({ to: (SITE_CONFIG.rootRedirect ?? "/") as never });
    }
    return { session };
  },
  component: AuthenticatedLayout,
});

const HOME_TABS: HomeTab[] = ["recent", "mine", "drafts"];

const homeRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/",
  beforeLoad: () => {
    if (SITE_CONFIG.rootRedirect) throw redirect({ to: SITE_CONFIG.rootRedirect as never });
  },
  // Optional on purpose: the default tab stays out of the URL, and every `<Link to="/">`
  // elsewhere in the app is spared a mandatory `search` prop.
  validateSearch: (search: Record<string, unknown>): { tab?: HomeTab } =>
    HOME_TABS.includes(search.tab as HomeTab) && search.tab !== "recent"
      ? { tab: search.tab as HomeTab }
      : {},
  component: function HomeRoute() {
    const { tab } = homeRoute.useSearch();
    return <HomeScene tab={tab ?? "recent"} />;
  },
});

/**
 * W10 §6 — the filters are part of the ADDRESS, so a filtered search is shareable and survives
 * a reload. Every key is optional and an unset one is OMITTED rather than serialised as empty:
 * `{}` is the default, which keeps `/search` itself a valid, clean URL.
 */
const searchRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/search",
  validateSearch: (search: Record<string, unknown>): SearchRouteParams => {
    const text = (key: string): string | undefined =>
      typeof search[key] === "string" && search[key] !== "" ? (search[key] as string) : undefined;
    const date = text("date");
    const status = text("status");
    return {
      ...(text("query") ? { query: text("query") } : {}),
      // Search-as-a-prompt S2: the ✨ toggle lives in the address too.
      ...(text("mode") === "ask" ? { mode: "ask" as const } : {}),
      ...(text("collection") ? { collection: text("collection") } : {}),
      ...(text("by") ? { by: text("by") } : {}),
      // Unknown values are dropped, not passed through: the server ignores them silently and
      // a filter chip that does nothing is worse than no chip.
      ...(date && SEARCH_DATE_FILTERS.includes(date as SearchDateFilter)
        ? { date: date as SearchDateFilter }
        : {}),
      ...(status
        ? {
            status: status
              .split(",")
              .filter((value): value is SearchStatus =>
                SEARCH_STATUSES.includes(value as SearchStatus),
              ),
          }
        : {}),
    };
  },
  component: function SearchRoute() {
    const params = searchRoute.useSearch();
    return <SearchScene params={params} />;
  },
});

const discoverRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/discover",
  component: DiscoverScene,
});

const collectionsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/collections",
  component: CollectionsScene,
});

const COLLECTION_TABS: CollectionTab[] = ["documents", "drafts", "archived"];

const collectionRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/collections/$collectionId",
  validateSearch: (search: Record<string, unknown>): { tab?: CollectionTab } =>
    COLLECTION_TABS.includes(search.tab as CollectionTab) && search.tab !== "documents"
      ? { tab: search.tab as CollectionTab }
      : {},
  component: function CollectionRoute() {
    const { collectionId } = collectionRoute.useParams();
    const { tab } = collectionRoute.useSearch();
    return <CollectionScene collectionId={collectionId} tab={tab ?? "documents"} />;
  },
});

const newsCollectionRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/collections/$collectionId/news",
  validateSearch: (search: Record<string, unknown>): { view?: string; ws?: string } => ({
    ...(search.view ? { view: safeNewsView(search.view) } : {}),
    ...(typeof search.ws === "string" ? { ws: search.ws } : {}),
  }),
  component: function NewsCollectionRoute() {
    const { collectionId } = newsCollectionRoute.useParams();
    const { view, ws } = newsCollectionRoute.useSearch();
    return (
      <WorkspaceNewsCollectionScene
        key={collectionId}
        collectionId={collectionId}
        view={view ?? "/news"}
        ws={ws}
      />
    );
  },
});

const documentsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/documents",
  component: DocumentsScene,
});

/**
 * W8 §F.4 decision 6 — the right drawer is linkable.
 *
 * kms's drawer could not be linked or reloaded, which is the single most-requested missing
 * affordance on a surface people sit on for minutes. The return type is OPTIONAL on purpose:
 * a `validateSearch` whose type has required members makes `search` mandatory on EVERY `Link`
 * to this route, estate-wide.
 */
const DOCUMENT_PANELS = ["conversation", "chat", "comments"] as const;
export type DocumentPanel = (typeof DOCUMENT_PANELS)[number];

const documentRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/documents/$documentId",
  validateSearch: (
    search: Record<string, unknown>,
  ): { panel?: DocumentPanel; c?: string; ws?: string } => ({
    ...(typeof search.ws === "string" ? { ws: search.ws } : {}),
    ...(DOCUMENT_PANELS.includes(search.panel as DocumentPanel)
      ? { panel: search.panel as DocumentPanel }
      : {}),
    ...(typeof search.c === "string" && search.c !== "" ? { c: search.c } : {}),
  }),
  component: function DocumentRoute() {
    const { documentId } = documentRoute.useParams();
    const { panel, c, ws } = documentRoute.useSearch();
    const { gate, hold } = useWorkspaceGate(ws);
    if (hold) return <WorkspaceGateScreen gate={gate} />;
    return (
      <DocumentScene
        documentId={documentId}
        {...(panel === undefined ? {} : { panel })}
        {...(c === undefined ? {} : { panelEntityId: c })}
      />
    );
  },
});

const documentEditRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/documents/$documentId/edit",
  component: function DocumentEditRoute() {
    const { documentId } = documentEditRoute.useParams();
    return <DocumentScene documentId={documentId} mode="edit" />;
  },
});

const documentHistoryRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/documents/$documentId/history",
  // W9 §A.2 — `?merge=<sessionId>` keeps an open merge linkable; an empty string means "open
  // the picker, no session chosen yet" (DivergenceBadge's "Merge revisions" button).
  validateSearch: (search: Record<string, unknown>): { revision?: string; merge?: string } => ({
    ...(typeof search.revision === "string" ? { revision: search.revision } : {}),
    ...(typeof search.merge === "string" ? { merge: search.merge } : {}),
  }),
  component: function DocumentHistoryRoute() {
    const { documentId } = documentHistoryRoute.useParams();
    const { revision, merge } = documentHistoryRoute.useSearch();
    return (
      <DocumentHistoryScene
        documentId={documentId}
        {...(revision === undefined ? {} : { revisionId: revision })}
        {...(merge === undefined ? {} : { mergeSessionId: merge })}
      />
    );
  },
});

const documentInsightsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/documents/$documentId/insights",
  component: function DocumentInsightsRoute() {
    const { documentId } = documentInsightsRoute.useParams();
    return <DocumentInsightsScene documentId={documentId} />;
  },
});

const draftsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/drafts",
  component: DraftsScene,
});

const archiveRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/archive",
  component: ArchiveScene,
});

const trashRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/trash",
  component: TrashScene,
});

/** `/profile` is the personal half of settings; keep one implementation, not two. */
const profileRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/profile",
  beforeLoad: () => {
    throw redirect({ to: "/settings/$section", params: { section: "profile" } });
  },
  component: () => null,
});

const settingsIndexRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/settings",
  beforeLoad: () => {
    throw redirect({ to: "/settings/$section", params: { section: "profile" } });
  },
  component: () => null,
});

/**
 * One route for all 28 settings sections. The registry decides which exist, what they are
 * called and who may see them — adding a section never touches this file.
 */
const settingsSectionRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/settings/$section",
  // `?help=setup` opens a section's help window on arrival (Telegram, 2026-10-17). All optional, so
  // no Link to a settings section has to carry `search`.
  validateSearch: (search: Record<string, unknown>): { help?: "setup"; tab?: string; pair?: string } => ({
    ...(search.help === "setup" ? { help: "setup" as const } : {}),
    ...(typeof search.tab === "string" ? { tab: search.tab } : {}),
    ...(typeof search.pair === "string" && /^[A-Fa-f0-9-]{10,11}$/.test(search.pair) ? { pair: search.pair } : {}),
  }),
  component: function SettingsSectionRoute() {
    const { section } = settingsSectionRoute.useParams();
    return <SettingsScene sectionId={section} />;
  },
});

/**
 * The run viewer (W2 §A.4). A route, not a modal — the kms viewer hosts a full-height
 * canvas, a navigation back-stack for child runs, and is a surface an operator sits on for
 * minutes; a modal cannot be linked, reloaded, or given proper height (D.1). The scene itself
 * keeps its own back-stack, so drilling into a child run never changes this route's params.
 */
const runDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/runs/$runId",
  // Optional on purpose (see the home route): the default tab stays out of the URL and no
  // `<Link to="/runs/$runId">` elsewhere grows a mandatory `search`. Listed here rather than
  // imported so a test that mocks the RunPage module still has a working route.
  validateSearch: (search: Record<string, unknown>): { tab?: RunTab } =>
    (["steps", "logs", "artifacts", "result", "reasoning"] as const).includes(search.tab as RunTab)
      ? { tab: search.tab as RunTab }
      : {},
  component: function RunDetailRoute() {
    const { runId } = runDetailRoute.useParams();
    const { tab } = runDetailRoute.useSearch();
    return <RunPage runId={runId} initialTab={tab} />;
  },
});

const ResearchScene = lazyRouteComponent(
  () => import("~/features/research/ResearchScene"),
  "ResearchScene",
);
const researchRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/research",
  // `context` = "<kind>:<id>" (document | collection | work-item) and `q` = the question to
  // start from — what every "Debate this …" button carries here (debate/README.md).
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    research?: string;
    prototype?: "rescue-rover";
    context?: string;
    q?: string;
    council?: string;
  } => {
    const q =
      typeof search.q === "string"
        ? search.q
        : typeof search.q === "number"
          ? String(search.q)
          : "";
    return {
      research: typeof search.research === "string" ? search.research : undefined,
      prototype: search.prototype === "rescue-rover" ? "rescue-rover" : undefined,
      context:
        typeof search.context === "string" && parseResearchContext(search.context)
          ? search.context
          : undefined,
      q: q.trim() ? q.slice(0, RESEARCH_QUESTION_PARAM_LIMIT) : undefined,
      // `council` = a council preset id (debate/README.md, "Councils"): the setup seats its roles on arrival.
      council:
        typeof search.council === "string" && search.council.trim()
          ? search.council.trim().slice(0, 60)
          : undefined,
    };
  },
  component: function ResearchRoute() {
    const search = researchRoute.useSearch();
    return (
      <ResearchScene
        initialResearchId={search.research}
        initialContext={parseResearchContext(search.context)}
        initialQuestion={search.q}
        initialCouncil={search.council}
        prototype={search.prototype}
      />
    );
  },
});

const reasoningHubRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/reasoning",
  validateSearch: parseReasoningLaunch,
  component: function ReasoningRoute() {
    const launch = reasoningHubRoute.useSearch();
    return <ReasoningHubScene key={JSON.stringify(launch)} launch={launch} />;
  },
});

/**
 * The flow studio (W3 §E.1). The param is the flow SLUG, not an id — every `ai-flows.*`
 * endpoint keys on the slug, and naming it `$flowId` would invite a lookup that does not exist.
 *
 * LAZY, for the same reason `worldRoute` is: `@xyflow/react` + `@dagrejs/dagre` are ~250 KB
 * that nothing outside this one route needs, and an eager import would put them on every
 * visitor's first paint of /documents.
 */
const flowStudioRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/flows/$slug/edit",
  component: lazyRouteComponent(
    () => import("~/features/flows/FlowStudioScene"),
    "FlowStudioScene",
  ),
});

/**
 * W4 §C.1 — the IDE console is a route, not a settings section.
 *
 * kms promoted it out of Settings for a reason worth keeping: settings is where you go to
 * CHANGE something, and there was nowhere to go to WATCH something. Everything here is an
 * observation of the workspace's connected editor windows.
 */
const consoleIndexRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/console",
  beforeLoad: () => {
    throw redirect({ to: "/console/ides" });
  },
  component: () => null,
});

/**
 * The legacy settings path. It is a redirect rather than a dead link because the
 * `ide.connected` notification's deep link already points at it in the wild — a static
 * segment beats `/settings/$section`, so this intercepts before the registry sees it.
 */
const connectedIdesRedirectRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/settings/connected-ides",
  beforeLoad: () => {
    throw redirect({ to: "/console/ides" });
  },
  component: () => null,
});

/**
 * BRONCO Opus 03 — thinking threads live on the Thinkers page as a tab for now (Opus 06 moves them into the assistant's
 * stall); this path is the one the Opus doc names, so it lands there.
 */
const thinkingThreadsRedirectRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/settings/agents/threads",
  beforeLoad: () => {
    throw redirect({
      to: "/settings/$section",
      params: { section: "agents" },
      search: { tab: "threads" } as never,
    });
  },
  component: () => null,
});

const ideConsoleRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/console/ides",
  component: function IdeConsoleRoute() {
    return <IdeConsoleScene />;
  },
});

/**
 * `/talk` — a conversation with the voice chosen in the header's model chooser (an IDE Claude
 * window or a workspace AI); feature requests there become PROPOSED Docit Work plans.
 */
const talkRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/talk",
  component: lazyRouteComponent(() => import("~/features/talk/TalkScene"), "TalkScene"),
});

const talkConversationRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/talk/$conversationId",
  component: lazyRouteComponent(() => import("~/features/talk/TalkScene"), "TalkScene"),
});

/** The param is the SESSION KEY — minted per activation by the window itself, not an id. */
const ideDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/console/ides/$sessionKey",
  component: lazyRouteComponent(
    () => import("~/features/console/IdeDetailScene"),
    "IdeDetailRoute",
  ),
});

/**
 * The first-run wizard as a real destination (§E-9). kms mounted it inside Home, which
 * coupled onboarding to Home's data loading and made it unreachable once dismissed; a route
 * can be linked, re-entered and skipped without any of that.
 */
const welcomeRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/welcome",
  component: lazyRouteComponent(() => import("~/features/welcome/WelcomeScene"), "WelcomeScene"),
});

const catchAllRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "$",
  component: NotFoundScene,
});

// fe-world (v9/P3) — the shared 3D information world: the same @docit/info-world package Pulse
// mounts at /planes/world. One layout grammar, two hosts, one world.
//
// LAZY, and this is load-bearing: three.js is ~1 MB of bundle, and eagerly importing it here made
// every visitor pay it on /documents and /settings where nothing 3D renders. The route-level
// import splits it into its own chunk that loads on first visit to /world only.
const worldRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/world",
  component: lazyRouteComponent(() => import("~/features/world/WorldPage"), "WorldPage"),
});

/**
 * The workspace thinker (2026-10-23): the workspace's main, multifaceted steward — three seats on three models grounded
 * in the workspace knowledge, five standing duties, channels and rules. Host-private (slate only, like /knowledge), so a
 * hand route rather than a registry manifest; lazy because nothing else needs it.
 */
const workspaceThinkerRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/thinker",
  component: lazyRouteComponent(
    () => import("~/features/thinkers/workspace/WorkspaceThinkerScene"),
    "WorkspaceThinkerScene",
  ),
});

const workspaceScheduleRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/thinker/schedule",
  component: lazyRouteComponent(
    () => import("~/features/thinkers/workspace/WorkspaceScheduleScene"),
    "WorkspaceScheduleScene",
  ),
});

// Workspace Knowledge (2026-10-11, workspace-knowledge README §4) — ONE area. A pathless layout
// gives every knowledge page the same header + tab bar (Overview · Browse · Tree · Space ·
// Diagnostics); each page keeps its own route, path and content. The layout is lazy like its
// pages: nothing here loads until a knowledge route is visited.
const knowledgeAreaRoute = createRoute({
  getParentRoute: () => authedRoute,
  id: "knowledge-area",
  component: lazyRouteComponent(
    () => import("~/features/knowledge/KnowledgeArea"),
    "KnowledgeAreaLayout",
  ),
});

/** The wisdom browser's search params — `/knowledge/browse`, and the old `/knowledge?…` links. */
type KnowledgeBrowseSearch = { view?: "list" | "graph"; kind?: string; id?: string };
const validateKnowledgeBrowseSearch = (search: Record<string, unknown>): KnowledgeBrowseSearch => ({
  ...(search.view === "graph" || search.view === "list" ? { view: search.view } : {}),
  ...(typeof search.kind === "string" ? { kind: search.kind } : {}),
  ...(typeof search.id === "string" ? { id: search.id } : {}),
});

// knowledge-space-3d W4 (spec §2.6) — the unified space control, document-focused. Same lazy
// discipline as worldRoute above: three.js must never load eagerly.
// knowledge-space-3d tree scopes (2026-08-18): /space (workspace tree) and
// /space/collection/$collectionId (collection tree) beside the per-document space. Static
// segments outrank the $docId param, so the three coexist. Same lazy discipline: three.js
// loads only when a space route is visited.
const workspaceSpaceRoute = createRoute({
  getParentRoute: () => knowledgeAreaRoute,
  path: "/space",
  component: lazyRouteComponent(() => import("~/features/space/SpacePage"), "WorkspaceSpacePage"),
});

// `/knowledge` is the area's Overview. It WAS the wisdom browser (now `/knowledge/browse`), and
// its deep links are in the wild — `?view=graph&kind=…&id=…` — so any of those params forwards
// there with the params intact.
const knowledgeSourceRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/knowledge/source/$collectionId",
  validateSearch: (search: Record<string, unknown>) => ({
    view: safeNewsView(search.view),
    ws: typeof search.ws === "string" ? search.ws : undefined,
  }),
  component: () => {
    const { collectionId } = knowledgeSourceRoute.useParams();
    const { view, ws } = knowledgeSourceRoute.useSearch();
    return <SourceLensScene collectionId={collectionId} view={view} ws={ws} />;
  },
});

const knowledgeRoute = createRoute({
  getParentRoute: () => knowledgeAreaRoute,
  path: "/knowledge",
  validateSearch: validateKnowledgeBrowseSearch,
  beforeLoad: ({ search }) => {
    if (search.view || search.kind || search.id) {
      throw redirect({ to: "/knowledge/browse", search, replace: true });
    }
  },
  component: lazyRouteComponent(
    () => import("~/features/knowledge/KnowledgeOverview"),
    "KnowledgeOverview",
  ),
});

// W9 §A.1.1 W1 — `view` is a route search param (kms kept its tab in `useState` and lost it
// on reload). `kind`/`id` deep-link straight into a node's neighbourhood in graph mode.
const knowledgeBrowseRoute = createRoute({
  getParentRoute: () => knowledgeAreaRoute,
  path: "/knowledge/browse",
  validateSearch: validateKnowledgeBrowseSearch,
  component: lazyRouteComponent(
    () => import("~/features/knowledge/KnowledgeBrowserPage"),
    "KnowledgeBrowserPage",
  ),
});

const knowledgeDiscoveriesRoute = createRoute({
  getParentRoute: () => knowledgeAreaRoute,
  path: "/knowledge/discoveries",
  validateSearch: (search: Record<string, unknown>) => ({
    ...(typeof search.finding === "string" ? { finding: search.finding } : {}),
    ...(typeof search.ws === "string" ? { ws: search.ws } : {}),
  }),
  component: lazyRouteComponent(
    () => import("~/features/knowledge/DiscoveriesPage"),
    "DiscoveriesPage",
  ),
});
const knowledgeBriefingsRoute = createRoute({
  getParentRoute: () => knowledgeAreaRoute,
  path: "/knowledge/briefings",
  validateSearch: (search: Record<string, unknown>) => ({
    ...(typeof search.forecast === "string" ? { forecast: search.forecast } : {}),
    ...(typeof search.edition === "string" ? { edition: search.edition } : {}),
    ...(typeof search.ws === "string" ? { ws: search.ws } : {}),
  }),
  component: lazyRouteComponent(
    () => import("~/features/knowledge/ForecastKnowledgePage"),
    "ForecastKnowledgePage",
  ),
});

// BRIEF-CGNI slice 2 — Triggers & questions (brief-cgni/opus-on-triggers.md): every trigger, subscription, rule and question
// of the workspace in one place. App-owned (slate-private scene; never a docit-features manifest).
const knowledgeTriggersRoute = createRoute({
  getParentRoute: () => knowledgeAreaRoute,
  path: "/knowledge/triggers",
  validateSearch: (search: Record<string, unknown>): { tab?: string; question?: string } => ({
    ...(typeof search.tab === "string" ? { tab: search.tab } : {}),
    ...(typeof search.question === "string" ? { question: search.question } : {}),
  }),
  component: lazyRouteComponent(
    () => import("~/features/knowledge/triggers/TriggersScene"),
    "TriggersScene",
  ),
});

// CNPY-8 — the knowledge canopy, read from the top. App-owned (canopy README §B5: NO registry
// involvement); the VS Code Index Status panel's "Open wisdom" button targets exactly this path.
const knowledgeTopdownRoute = createRoute({
  getParentRoute: () => knowledgeAreaRoute,
  path: "/knowledge/topdown",
  validateSearch: (search: Record<string, unknown>): { scope?: string; node?: string } => ({
    ...(typeof search.scope === "string" ? { scope: search.scope } : {}),
    ...(typeof search.node === "string" ? { node: search.node } : {}),
  }),
  component: lazyRouteComponent(() => import("~/features/knowledge/TopdownPage"), "TopdownPage"),
});

// CWAY — the PROJECT narrated top-down (`wisdom.topdown`): components → the documents that describe
// them → what was understood → what is believed → what has been measured to drive what. App-owned
// like the canopy (not registry-managed). `?component=<path>` is the stable deep link: it opens that
// row — the document page's "Describes code" panel and the VS Code Index panel point here.
const knowledgeProjectRoute = createRoute({
  getParentRoute: () => knowledgeAreaRoute,
  path: "/knowledge/project",
  validateSearch: (search: Record<string, unknown>): { component?: string } => ({
    ...(typeof search.component === "string" ? { component: search.component } : {}),
  }),
  component: lazyRouteComponent(
    () => import("~/features/knowledge/ProjectTopDownPage"),
    "ProjectTopDownPage",
  ),
});

// The knowledge pipeline, structural + visual (2026-10-05) — admin-only server-side (knowledge.debug).
const compressionLabRoute = createRoute({
  getParentRoute: () => knowledgeAreaRoute,
  path: "/knowledge/compression",
  component: lazyRouteComponent(() => import("~/features/knowledge/CompressionLab"), "CompressionLab"),
});

const deduplicatorLabRoute = createRoute({
  getParentRoute: () => knowledgeAreaRoute,
  path: "/knowledge/deduplicator",
  component: lazyRouteComponent(() => import("~/features/knowledge/DeduplicatorLab"), "DeduplicatorLab"),
});

const contextRequestsRoute = createRoute({
  getParentRoute: () => knowledgeAreaRoute,
  path: "/knowledge/context",
  component: lazyRouteComponent(() => import("~/features/knowledge/ContextRequests"), "ContextRequests"),
});

const knowledgeDebugRoute = createRoute({
  getParentRoute: () => knowledgeAreaRoute,
  path: "/knowledge/debug",
  validateSearch: (search: Record<string, unknown>): { scope?: string; node?: string } => ({
    ...(typeof search.scope === "string" ? { scope: search.scope } : {}),
    ...(typeof search.node === "string" ? { node: search.node } : {}),
  }),
  component: lazyRouteComponent(
    () => import("~/features/knowledge/KnowledgeDebugPage"),
    "KnowledgeDebugPage",
  ),
});

// The old estate-wide sweep feed became the area's Activity (§4): the path is kept — static, so
// it still outranks /space/$docId — and forwards to the Overview's Activity section.
const sweepWatchRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/space/sweep",
  beforeLoad: () => {
    throw redirect({ to: "/knowledge", hash: "activity", replace: true });
  },
});

const collectionSpaceRoute = createRoute({
  getParentRoute: () => knowledgeAreaRoute,
  path: "/space/collection/$collectionId",
  component: lazyRouteComponent(() => import("~/features/space/SpacePage"), "CollectionSpacePage"),
});

const spaceRoute = createRoute({
  getParentRoute: () => knowledgeAreaRoute,
  path: "/space/$docId",
  component: lazyRouteComponent(() => import("~/features/space/SpacePage"), "SpacePage"),
});

// The template studio (Docit Work, 2026-09-23) — registered HERE rather than in the shared `work`
// manifest, because it draws on slate's own flow canvas: in the shared catalog its loader had to
// resolve in every host, and coil (no flow editor) stopped building. A static segment, so it
// outranks the registry's /work/$key.
const workTemplatesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/work/templates",
  component: lazyRouteComponent(
    () => import("~/features/flows/TemplateStudioScene"),
    "TemplateStudioScene",
  ),
});

// The live observatory shares Slate's 3D knowledge renderer with the IDE webview.
const workActivityRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/work/activity",
  component: lazyRouteComponent(() => import("~/features/activity/ActivityPage"), "ActivityPage"),
});

/* <feature-routes:generated>
 * Routes contributed by the features this site enables. GENERATED from
 * packages/docit-features (catalog.ts + sites.ts) — do not hand-edit; run
 * `pnpm routes:sync` after changing which features the site mounts.
 */
const featureLoad = (path: string) => loaderFor(SITE, path);

const publicFeatureRoutes = [
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/u/$slug",
    component: lazyRouteComponent(featureLoad("/u/$slug")),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/q/$token",
    component: lazyRouteComponent(featureLoad("/q/$token")),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/p/$urlId",
    component: lazyRouteComponent(featureLoad("/p/$urlId")),
  }),
] as const;

const authedFeatureRoutes = [
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/market",
    component: lazyRouteComponent(featureLoad("/market")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/market/new",
    component: lazyRouteComponent(featureLoad("/market/new")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/market/post",
    component: lazyRouteComponent(featureLoad("/market/post")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/market/me",
    component: lazyRouteComponent(featureLoad("/market/me")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/market/profile",
    component: lazyRouteComponent(featureLoad("/market/profile")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/market/interview",
    component: lazyRouteComponent(featureLoad("/market/interview")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/market/listing/$id",
    component: lazyRouteComponent(featureLoad("/market/listing/$id")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/market/listing/$id/edit",
    component: lazyRouteComponent(featureLoad("/market/listing/$id/edit")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/messages",
    component: lazyRouteComponent(featureLoad("/messages")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/people",
    component: lazyRouteComponent(featureLoad("/people")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/people/$userId",
    component: lazyRouteComponent(featureLoad("/people/$userId")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/hr",
    component: lazyRouteComponent(featureLoad("/hr")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/subscriptions",
    component: lazyRouteComponent(featureLoad("/subscriptions")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/bench",
    component: lazyRouteComponent(featureLoad("/bench")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/work",
    component: lazyRouteComponent(featureLoad("/work")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/work/inbox",
    component: lazyRouteComponent(featureLoad("/work/inbox")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/work/todo",
    component: lazyRouteComponent(featureLoad("/work/todo")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/plainly",
    component: lazyRouteComponent(featureLoad("/plainly")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/work/now",
    component: lazyRouteComponent(featureLoad("/work/now")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/work/sprints",
    component: lazyRouteComponent(featureLoad("/work/sprints")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/work/aggregate",
    component: lazyRouteComponent(featureLoad("/work/aggregate")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/work/plan/$planRef",
    component: lazyRouteComponent(featureLoad("/work/plan/$planRef")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/work/$key",
    component: lazyRouteComponent(featureLoad("/work/$key")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/mail",
    component: lazyRouteComponent(featureLoad("/mail")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/mail/settings",
    component: lazyRouteComponent(featureLoad("/mail/settings")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/mail/$id",
    component: lazyRouteComponent(featureLoad("/mail/$id")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/repositories",
    component: lazyRouteComponent(featureLoad("/repositories")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/repo/$connectionId",
    component: lazyRouteComponent(featureLoad("/repo/$connectionId")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/knowledge/stories",
    component: lazyRouteComponent(featureLoad("/knowledge/stories")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/knowledge/explore",
    component: lazyRouteComponent(featureLoad("/knowledge/explore")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/knowledge/analysis",
    component: lazyRouteComponent(featureLoad("/knowledge/analysis")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/knowledge/analysis/$view",
    component: lazyRouteComponent(featureLoad("/knowledge/analysis/$view")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/knowledge/visuals",
    component: lazyRouteComponent(featureLoad("/knowledge/visuals")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/knowledge/visuals/causal-map",
    component: lazyRouteComponent(featureLoad("/knowledge/visuals/causal-map")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/knowledge/visuals/$viewId",
    component: lazyRouteComponent(featureLoad("/knowledge/visuals/$viewId")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/briefing/calendar",
    component: lazyRouteComponent(featureLoad("/briefing/calendar")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/briefing/magazine",
    component: lazyRouteComponent(featureLoad("/briefing/magazine")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/briefing/perspectives",
    component: lazyRouteComponent(featureLoad("/briefing/perspectives")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/briefing/visuals",
    component: lazyRouteComponent(featureLoad("/briefing/visuals")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/briefing",
    component: lazyRouteComponent(featureLoad("/briefing")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/briefing/tree",
    component: lazyRouteComponent(featureLoad("/briefing/tree")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/briefing/canopy",
    component: lazyRouteComponent(featureLoad("/briefing/canopy")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/briefing/topics",
    component: lazyRouteComponent(featureLoad("/briefing/topics")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/briefing/consequences",
    component: lazyRouteComponent(featureLoad("/briefing/consequences")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/briefing/saved",
    component: lazyRouteComponent(featureLoad("/briefing/saved")),
  }),
  createRoute({
    getParentRoute: () => authedRoute,
    path: "/briefing/hidden",
    component: lazyRouteComponent(featureLoad("/briefing/hidden")),
  }),
] as const;
/* </feature-routes:generated> */

const routeTree = rootRoute.addChildren([
  loginRoute,
  logoutRoute,
  callbackRoute,
  authCallbackRoute,
  selectWorkspaceRoute,
  sharedDocumentRoute,
  ...publicFeatureRoutes,
  authedRoute.addChildren([
    homeRoute,
    searchRoute,
    discoverRoute,
    collectionsRoute,
    collectionRoute,
    newsCollectionRoute,
    documentsRoute,
    documentRoute,
    documentEditRoute,
    documentHistoryRoute,
    documentInsightsRoute,
    draftsRoute,
    archiveRoute,
    trashRoute,
    profileRoute,
    worldRoute,
    workspaceThinkerRoute,
    workspaceScheduleRoute,
    knowledgeSourceRoute,
    knowledgeAreaRoute.addChildren([
      knowledgeRoute,
      knowledgeBrowseRoute,
      knowledgeBriefingsRoute,
      knowledgeDiscoveriesRoute,
      knowledgeTopdownRoute,
      knowledgeTriggersRoute,
      knowledgeProjectRoute,
      knowledgeDebugRoute,
      compressionLabRoute,
      deduplicatorLabRoute,
      contextRequestsRoute,
      workspaceSpaceRoute,
      collectionSpaceRoute,
      spaceRoute,
    ]),
    sweepWatchRoute,
    workTemplatesRoute,
    workActivityRoute,
    ...authedFeatureRoutes,
    settingsIndexRoute,
    settingsSectionRoute,
    runDetailRoute,
    reasoningHubRoute,
    researchRoute,
    flowStudioRoute,
    // W4: the console is a watching surface, so it sits beside the scenes rather than under
    // /settings — and the legacy settings path redirects into it, because that deep link is
    // already in the wild inside `ide.connected` notifications.
    connectedIdesRedirectRoute,
    thinkingThreadsRedirectRoute,
    consoleIndexRoute,
    ideConsoleRoute,
    ideDetailRoute,
    talkRoute,
    talkConversationRoute,
    welcomeRoute,
    catchAllRoute,
  ]),
]);

export interface AppRouterOptions {
  /** Injected by tests so navigation does not need a real browser history. */
  history?: RouterHistory;
}

export function createAppRouter(context: RouterContext, options: AppRouterOptions = {}) {
  return createRouter({
    routeTree,
    context,
    defaultPreload: "intent",
    defaultPreloadStaleTime: 0,
    defaultNotFoundComponent: NotFoundScene,
    ...(options.history ? { history: options.history } : {}),
  });
}

export type AppRouter = ReturnType<typeof createAppRouter>;

/**
 * The public contract of the route table, used by the navigation registry and its test. The
 * app's own paths are literal; the feature paths come from the registry, so a feature turned
 * on or off in `sites.ts` cannot leave this list stale.
 */
export const appRoutePaths = [
  ...routePathsFor(SITE),
  "/login",
  "/logout",
  "/callback",
  "/auth/callback",
  "/select-workspace",
  "/s/$shareId",
  "/",
  "/search",
  "/discover",
  "/world",
  "/knowledge",
  "/knowledge/browse",
  "/knowledge/briefings",
  "/knowledge/topdown",
  "/knowledge/triggers",
  "/knowledge/discoveries",
  "/knowledge/project",
  "/knowledge/debug",
  "/knowledge/compression",
  "/knowledge/deduplicator",
  "/space",
  "/space/sweep",
  "/space/collection/$collectionId",
  "/space/$docId",
  "/work/templates",
  "/work/activity",
  "/collections",
  "/collections/$collectionId",
  "/documents",
  "/documents/$documentId",
  "/documents/$documentId/edit",
  "/documents/$documentId/history",
  "/documents/$documentId/insights",
  "/drafts",
  "/archive",
  "/trash",
  "/profile",
  "/settings",
  "/settings/$section",
  "/runs/$runId",
  "/reasoning",
  "/research",
  "/flows/$slug/edit",
  "/console",
  "/console/ides",
  "/console/ides/$sessionKey",
  "/talk",
  "/talk/$conversationId",
  "/thinker",
  "/thinker/schedule",
  "/settings/agents/threads",
  "/settings/connected-ides",
  "/welcome",
];

declare module "@tanstack/react-router" {
  interface Register {
    router: AppRouter;
  }
}
